import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import { useOutletContext } from 'react-router-dom';
import SlidingDrawer from '../../components/SlidingDrawer';

const MinisterQuestions = () => {
    const { currentDept } = useOutletContext();
    const [questions, setQuestions] = useState([]);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [answer, setAnswer] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (currentDept) fetchQuestions();
    }, [currentDept]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/questions`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { department: currentDept.id }
            });
            setQuestions(res.data);
        } catch (err) {
            console.error('Failed to fetch questions');
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = (q) => {
        setSelectedQuestion(q);
        setAnswer(q.answerText || '');
        setIsDrawerOpen(true);
    };

    const confirmSubmit = () => {
        if (!answer.trim()) return Swal.fire('Required', 'Response content cannot be empty', 'warning');

        Swal.fire({
            title: 'Confirm Submission?',
            text: "This response will be recorded in the legislative database and sent to the Secretariat for review. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#C0722C',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Submit Response'
        }).then((result) => {
            if (result.isConfirmed) {
                handleSubmit();
            }
        });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/minister/questions/${selectedQuestion._id}/reply`, {
                answerText: answer
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire('Success', 'Response submitted. Awaiting Secretariat review and publication.', 'success');
            setIsDrawerOpen(false);
            fetchQuestions();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Submission failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const isOverdue = (assignedAt) => {
        if (!assignedAt) return false;
        return (new Date() - new Date(assignedAt)) > (7 * 24 * 60 * 60 * 1000);
    };

    const filteredQuestions = questions.filter(q =>
        q.subject.toLowerCase().includes(filter.toLowerCase()) ||
        q._id.toLowerCase().includes(filter.toLowerCase()) ||
        (q.questionText && q.questionText.toLowerCase().includes(filter.toLowerCase()))
    );

    const DataBlock = ({ label, value }) => (
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#F9F9F9', borderRadius: '12px', border: '1px solid #F0F0F0' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>
                {label}
            </label>
            <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '600', lineHeight: '1.5' }}>
                {value || 'N/A'}
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: '"Outfit", sans-serif', maxWidth: '1400px', margin: '0 auto', overflowX: 'hidden' }}>
            <style>
                {`
                .custom-input {
                    width: 100%;
                    padding: 16px 20px;
                    border-radius: 12px;
                    border: 1.5px solid #E5E5E5;
                    font-size: 16px;
                    box-sizing: border-box;
                    outline: none;
                    transition: all 0.3s ease;
                    background-color: #FAFAFA;
                    font-family: inherit;
                    color: #333;
                    -webkit-appearance: none;
                    appearance: none;
                }
                .custom-input:focus {
                    border-color: #C0722C;
                    box-shadow: 0 0 0 4px rgba(192, 114, 44, 0.1);
                    background-color: #fff;
                }
                .shimmer-loader {
                    border: 4px solid #F3F3F3;
                    border-top: 4px solid #C0722C;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin-loader 1s linear infinite;
                }
                @keyframes spin-loader {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse-slow {
                    0%, 100% { transform: scale(1); opacity: 0.2; }
                    50% { transform: scale(1.1); opacity: 0.5; }
                }
                `}
            </style>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800', color: '#2C1A10' }}>Legislative Inquiries</h1>
                    <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>
                        Active Jurisdiction: <span style={{ color: '#2C1A10', fontWeight: '700' }}>{currentDept?.name}</span>
                    </p>
                </div>
            </div>

            <div style={{ position: 'relative', marginBottom: '45px' }}>
                <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', zIndex: 2 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>
                <input
                    type="text"
                    placeholder="Search by Query Identity, Subject, or Content..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="custom-input"
                    style={{ paddingLeft: '48px' }}
                />
            </div>

            <div style={{
                backgroundColor: 'white',
                borderRadius: '28px',
                boxShadow: '0 15px 45px rgba(0,0,0,0.03)',
                overflow: 'hidden',
                border: '1px solid #F0F0F0',
                minHeight: '400px'
            }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '20px' }}>
                        <div className="shimmer-loader"></div>
                        <div style={{ fontSize: '11px', color: '#888', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase' }}>Synchronizing Repository...</div>
                    </div>
                ) : filteredQuestions.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '450px',
                        textAlign: 'center',
                        animation: 'fadeIn 0.6s ease-out'
                    }}>
                        <div style={{
                            width: '120px',
                            height: '120px',
                            backgroundColor: '#FDF8F3',
                            borderRadius: '45px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#C0722C',
                            marginBottom: '30px',
                            position: 'relative'
                        }}>
                            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="8" y1="13" x2="16" y2="13"></line>
                                <line x1="8" y1="17" x2="14" y2="17"></line>
                            </svg>
                        </div>
                        <h3 style={{ color: '#2C1A10', margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800' }}>Registry is Clear</h3>
                        <p style={{ color: '#888', fontSize: '16px', margin: 0, maxWidth: '350px', lineHeight: '1.6' }}>
                            No entries found for "<strong>{filter}</strong>" in the legislative ledger.
                        </p>
                        <button
                            onClick={() => setFilter('')}
                            style={{
                                marginTop: '35px',
                                padding: '14px 28px',
                                backgroundColor: 'transparent',
                                border: '2px solid #C0722C',
                                color: '#C0722C',
                                borderRadius: '16px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            Reset Search Registry
                        </button>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', textAlign: 'left' }}>
                        <thead style={{ backgroundColor: '#FDF8F3' }}>
                            <tr style={{ color: '#888', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                                <th style={{ padding: '25px 30px' }}>Query Identity</th>
                                <th style={{ padding: '25px 30px' }}>Status Matrix</th>
                                <th style={{ padding: '25px 30px' }}>Timeline</th>
                                <th style={{ padding: '25px 30px', textAlign: 'right' }}>Directives</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQuestions.map(q => {
                                const overdue = isOverdue(q.assignedAt) && q.status !== 'ANSWERED' && q.status !== 'PUBLISHED';
                                const notified = q.notified && q.status !== 'ANSWERED';

                                return (
                                    <tr
                                        key={q._id}
                                        onClick={() => handleOpen(q)}
                                        style={{
                                            borderBottom: '1px solid #F5F5F5',
                                            cursor: 'pointer',
                                            transition: 'background 0.2s',
                                            backgroundColor: notified ? '#FFFBF5' : (overdue ? '#FFF9F9' : '#fff')
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#FAFAFA';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = notified ? '#FFFBF5' : (overdue ? '#FFF9F9' : '#fff');
                                        }}
                                    >
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#C0722C', opacity: 0.8 }}>#{q._id.slice(-6).toUpperCase()}</div>
                                                <div style={{ fontWeight: '800', color: '#2C1A10', fontSize: '17px' }}>{q.subject}</div>
                                                <div style={{ fontSize: '14px', color: '#777', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {q.questionText}
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                                                <span style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '10px',
                                                    backgroundColor: q.status === 'PUBLISHED' ? '#E8F5E9' : (q.status === 'ANSWERED' ? '#E3F2FD' : (overdue ? '#FFEBEE' : '#FFF3E0')),
                                                    color: q.status === 'PUBLISHED' ? '#2E7D32' : (q.status === 'ANSWERED' ? '#1565C0' : (overdue ? '#C62828' : '#E65100')),
                                                    fontSize: '11px',
                                                    fontWeight: '800',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                    border: `1px solid ${q.status === 'PUBLISHED' ? 'rgba(46, 125, 50, 0.2)' : (q.status === 'ANSWERED' ? 'rgba(21, 101, 192, 0.2)' : (overdue ? 'rgba(198, 40, 40, 0.2)' : 'rgba(230, 81, 0, 0.2)'))}`
                                                }}>{q.status === 'ASSIGNED' ? 'Pending Reply' : q.status}</span>
                                                {overdue && <div style={{ fontSize: '10px', color: '#C62828', fontWeight: '800', letterSpacing: '0.5px' }}>SLA BREACH</div>}
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>
                                                {new Date(q.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#999', fontWeight: '500', marginTop: '4px' }}>Assigned Protocol</div>
                                        </td>
                                        <td style={{ padding: '25px 30px', textAlign: 'right' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpen(q);
                                                }}
                                                style={{
                                                    padding: '10px 20px',
                                                    backgroundColor: q.status === 'PUBLISHED' || q.status === 'ANSWERED' ? '#FDF8F3' : '#2C1A10',
                                                    color: q.status === 'PUBLISHED' || q.status === 'ANSWERED' ? '#C0722C' : '#fff',
                                                    border: q.status === 'PUBLISHED' || q.status === 'ANSWERED' ? '1px solid #F0E8DB' : 'none',
                                                    borderRadius: '12px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '700',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {q.status === 'PUBLISHED' || q.status === 'ANSWERED' ? 'View Record' : 'Submit Reply'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <SlidingDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Legislative Response Protocol">
                {selectedQuestion && (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                            <span style={{
                                padding: '8px 18px', borderRadius: '14px',
                                backgroundColor: selectedQuestion.status === 'PUBLISHED' ? '#E8F5E9' : (selectedQuestion.status === 'ANSWERED' ? '#E3F2FD' : '#FDF8F3'),
                                color: selectedQuestion.status === 'PUBLISHED' ? '#2E7D32' : (selectedQuestion.status === 'ANSWERED' ? '#1565C0' : '#C0722C'),
                                fontSize: '11px', fontWeight: '900', textTransform: 'uppercase',
                                border: `1px solid ${selectedQuestion.status === 'PUBLISHED' ? 'rgba(46, 125, 50, 0.2)' : (selectedQuestion.status === 'ANSWERED' ? 'rgba(21, 101, 192, 0.2)' : 'rgba(192, 114, 44, 0.2)')}`
                            }}>
                                {selectedQuestion.status}
                            </span>
                            <span style={{ fontSize: '12px', color: '#999', fontWeight: '700', letterSpacing: '0.5px' }}>
                                PRIORITY: <span style={{ color: isOverdue(selectedQuestion.assignedAt) ? '#C62828' : '#2E7D32' }}>{isOverdue(selectedQuestion.assignedAt) ? 'CRITICAL' : 'STANDARD'}</span>
                            </span>
                        </div>

                        <DataBlock label="Subject Matter" value={selectedQuestion.subject} />

                        <div style={{ marginBottom: '30px', padding: '25px', backgroundColor: '#FDF8F3', borderRadius: '24px', border: '1px solid rgba(192, 114, 44, 0.1)' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: '900', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '15px' }}>
                                Official Inquiry Text
                            </label>
                            <div style={{ fontSize: '16px', color: '#2C1A10', lineHeight: '1.8', whiteSpace: 'pre-line', fontWeight: '500' }}>
                                {selectedQuestion.questionText}
                            </div>
                            <div style={{ marginTop: '20px', fontSize: '13px', color: '#888', fontWeight: '600', borderTop: '1px solid rgba(192, 114, 44, 0.1)', paddingTop: '15px', display: 'flex', justifyContent: 'space-between' }}>
                                <span>Proponent: <span style={{ color: '#2C1A10' }}>{selectedQuestion.memberName}</span></span>
                                <span>Ref: #{selectedQuestion._id.slice(-6).toUpperCase()}</span>
                            </div>
                        </div>

                        {selectedQuestion.status === 'PUBLISHED' || selectedQuestion.status === 'ANSWERED' ? (
                            <div style={{ marginBottom: '30px', padding: '25px', backgroundColor: '#F5FAF6', borderRadius: '24px', border: '1px solid rgba(46, 125, 50, 0.1)' }}>
                                <label style={{ display: 'block', fontSize: '10px', fontWeight: '900', color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '15px' }}>
                                    Recorded Departmental Response
                                </label>
                                <div style={{ fontSize: '16px', color: '#1B5E20', lineHeight: '1.8', whiteSpace: 'pre-line', fontWeight: '600' }}>
                                    {selectedQuestion.answerText}
                                </div>
                                <div style={{ fontSize: '12px', color: '#2E7D32', marginTop: '15px', textAlign: 'right', fontWeight: '700', opacity: 0.7 }}>
                                    AUTHENTICATED ON: {new Date(selectedQuestion.answeredAt).toLocaleString()}
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <label style={{ display: 'block', marginBottom: '10px', fontSize: '11px', fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Official Draft Response
                                </label>
                                <textarea
                                    value={answer}
                                    onChange={e => setAnswer(e.target.value)}
                                    placeholder="Enter the official department response for the legislative record..."
                                    style={{
                                        width: '100%', height: '280px', padding: '25px', borderRadius: '24px',
                                        border: '1.5px solid #E5E5E5', marginBottom: '25px', fontSize: '16px', lineHeight: '1.7',
                                        fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                                        backgroundColor: '#FAFAFA', color: '#2C1A10', transition: 'all 0.3s ease',
                                        boxSizing: 'border-box' // CRITICAL: Fixes overflow
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = '#C0722C'; e.target.style.backgroundColor = '#fff'; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.backgroundColor = '#FAFAFA'; }}
                                />

                                <div style={{ padding: '20px', backgroundColor: '#FFF4E5', borderRadius: '18px', marginBottom: '35px', border: '1px solid rgba(230, 81, 0, 0.1)' }}>
                                    <div style={{ display: 'flex', gap: '15px' }}>
                                        <div style={{ color: '#E65100' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#E65100', lineHeight: '1.6', fontWeight: '600' }}>
                                            <strong>Verification Required:</strong> This response will be subjected to Secretariat review. Once confirmed, editing capability will be revoked.
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <button
                                        onClick={confirmSubmit}
                                        disabled={submitting}
                                        style={{
                                            flex: 2, padding: '18px', backgroundColor: '#2C1A10', color: 'white', border: 'none',
                                            borderRadius: '16px', fontWeight: '900', cursor: 'pointer', fontSize: '16px',
                                            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1)', opacity: submitting ? 0.7 : 1,
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                                        }}
                                        onMouseEnter={e => !submitting && (e.currentTarget.style.transform = 'translateY(-3px)')}
                                        onMouseLeave={e => !submitting && (e.currentTarget.style.transform = 'translateY(0)')}
                                    >
                                        {submitting ? 'Authenticating...' : 'Authenticate & Submit'}
                                    </button>
                                    <button
                                        onClick={() => setIsDrawerOpen(false)}
                                        style={{
                                            flex: 1, padding: '18px', backgroundColor: '#fff', color: '#666',
                                            border: '1.5px solid #E5E5E5', borderRadius: '16px', fontWeight: '800',
                                            cursor: 'pointer', fontSize: '16px', transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={e => e.target.style.backgroundColor = '#F9F9F9'}
                                        onMouseLeave={e => e.target.style.backgroundColor = '#fff'}
                                    >
                                        Later
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </SlidingDrawer>
        </div>
    );
};

export default MinisterQuestions;
